import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewEncapsulation, ViewChild, ElementRef, OnChanges, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, ValidationErrors } from '@angular/forms';
import { MatSelectChange, MatOption } from '@angular/material';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { untilDestroyed } from 'ngx-take-until-destroy';

import { SelectedCard, Card } from '@awp/nac/card-check/api';

@Component({
  selector: 'ap-bin-check',
  templateUrl: './bin-check.component.html',
  host: {
    'class': 'ap-bin-check',
  },
  encapsulation: ViewEncapsulation.None
})
export class BinCheckComponent implements OnInit, OnDestroy, OnChanges {

  @Input() title: string;
  @Input() binLabel: string;
  @Input() binNumberControlHeader: string;
  @Input() currentBin: string;
  @Input() checkBinLabel: string;
  @Input() cardNotFound: string;
  @Input() cardSelectLabel: string;
  @Input() selectCardFromLabel: string;
  @Input() resetCardLabel: string;
  @Input() showButton: boolean = true;
  @Input() emitOnSelectionChange: boolean = false;
  @Input() validationMessages: { [key: string]: string };
  @Input() cards: Card[];
  @Input() displayOption: 'Search' | 'Bin' | 'Card' = 'Bin';
  @Input() noBinError: boolean = false;
  @Input() alternativeBinError: boolean = false;

  @Output() selected = new EventEmitter<SelectedCard>();
  @Output() binSearch = new EventEmitter<string>();
  @Output() reset = new EventEmitter();
  @Output() clearNoBinError = new EventEmitter();
  @Output() clearAlternativeBinError = new EventEmitter();

  @ViewChild('cardSelectInput', { read: ElementRef, static: false }) cardInputRef: ElementRef;
  @ViewChildren(MatOption) matOptions: QueryList<MatOption>;

  form: FormGroup;

  get binNumberControl(): FormControl {
    return this.form.get('binNumber') as FormControl;
  }
  get cardTypeControl(): FormControl {
    return this.form.get('cardType') as FormControl;
  }

  constructor(formBuilder: FormBuilder) {
    this.createForm(formBuilder);
  }

  ngOnInit() {
    fromEvent(window, 'message')
      .pipe(
        filter(p => (<any>p).origin === window.location.origin
                    && ((<any>p).data != null)
                    && (<any>p).data.action === 'click'),
        map(p => (<any>p).data),
        untilDestroyed(this)
      )
      .subscribe(p => {
        if (p.data === 'select') {
          this.selectCard();
        } else if (p.data === 'reset') {
          this.resetDisplay();
        }
      });
  }

  ngOnDestroy(): void { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.displayOption != null && changes.displayOption.currentValue === 'Card') {
      setTimeout(() => {
          if (this.cardInputRef) {
            this.cardInputRef.nativeElement.focus();
          }
        }, 500);
    }
  }

  onDropdown(isOpen: boolean): void {
    if (isOpen && this.matOptions != null && this.matOptions.length > 0) {
      this.matOptions.first.setInactiveStyles();
    }
  }

  private createForm(formBuilder: FormBuilder): void {
    this.form = formBuilder.group({
      binNumber: [
        this.currentBin,
        [
          Validators.required,
          Validators.pattern('^[0-9]*$'),
          Validators.maxLength(6),
          Validators.minLength(6),
          this.binValid.bind(this)
        ]
      ],
      cardType: [null, Validators.required]
    });
  }

  binValid(): ValidationErrors | null {
    return this.alternativeBinError ? { 'alternative-bin': true } : (this.noBinError ? { 'no-bin': true } : null);
  }

  binCheck(): void {
    this.binNumberControl.markAsTouched();

    if (this.binNumberControl.valid) {
      this.binSearch.emit(this.binNumberControl.value);
    }
  }

  clearBin(): void {
    this.binNumberControl.reset(null);
    this.noBinError = false;
    this.alternativeBinError = false;
    this.binNumberControl.updateValueAndValidity();
    this.reset.emit();
  }

  onKeydown(): void { // Need to reset noBinError here before validation is run
    if(this.noBinError) {
      this.clearNoBinError.emit();
    }
    if (this.alternativeBinError) {
      this.clearAlternativeBinError.emit();
    }
    this.noBinError = false;
    this.alternativeBinError = false;
  }

  resetDisplay(): void {
    this.reset.emit();
    this.form.reset();
  }

  selectCard(): void {
    // Need to show all cards here.
    this.form.get('binNumber').setValue(null);
    this.binSearch.emit('');
  }

  submit(): void {
    this.selected.emit(this.form.value);
  }

  onSelectionChanged(args: MatSelectChange): void {
    if (this.emitOnSelectionChange && args.value != null) {
      this.selected.emit(this.form.value);
    }
  }
}
